import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Container,
  FormGroup,
  Label,
  Card,
  Spinner,
} from "reactstrap";
import { GetCategories } from "network/user";
import CustomPagination from "components/common/Pagination";
import { ShowLoadingScreen } from "components/common/Loading";
import { ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { GetSectionsApi } from "network/user";
import { warningToaster } from "components/common/toast";
import { ImportCicProductsApi, SuggestCategorySectionsAPI } from "network/user";
import { mergeSuggestions } from "./cicSuggest";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { successToaster } from "components/common/toast";

const CategoryTableCic = () => {
  const { t } = useTranslation("products");
  const [categoriesData, setCategoriesData] = useState([]);
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState([]);
  const [doubts, setDoubts] = useState({}); // category_id -> perche' l'AI non e' sicura
  const [suggesting, setSuggesting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategory, setIsLoadingCategory] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("white-content")
  );
  const pageSize = 10;
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("white-content"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [setIsDarkMode]);
  const fetchColumns = async () => {
    try {
      setIsLoading(true);
      const response = await GetSectionsApi();
      if (response.success) {
        setSections(response?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchColumns();
  }, []);
  const fetchCategories = async (page) => {
    setIsLoadingCategory(true);

    try {
      const response = await GetCategories(page, pageSize);
      if (response.success) {
        setCategoriesData(response?.data?.categories);
        setTotalPages(response?.data?.total_pages);
        setIsLoadingCategory(false);
      }
    } catch (error) {
      setIsLoadingCategory(false);
      ErrorToaster(handleError(error?.status_code, error?.message));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchCategories(currentPage);
  }, [currentPage]);

  const toggleCheckbox = (categoryId, categoryName, sectionId) => {
    setSelected((prev) => {
      const categoryIndex = prev.findIndex(
        (item) => item.category_id === categoryId
      );

      if (categoryIndex !== -1) {
        const updated = [...prev];
        const sectionExists =
          updated[categoryIndex].section_ids.includes(sectionId);

        if (sectionExists) {
          updated[categoryIndex].section_ids = updated[
            categoryIndex
          ].section_ids.filter((id) => id !== sectionId);

          if (updated[categoryIndex].section_ids.length === 0) {
            updated.splice(categoryIndex, 1);
          }

          return updated;
        } else {
          updated[categoryIndex].section_ids.push(sectionId);
          return updated;
        }
      } else {
        return [
          ...prev,
          {
            category_id: categoryId,
            category_name: categoryName,
            section_ids: [sectionId],
          },
        ];
      }
    });
  };

  const suggestWithAI = async () => {
    setSuggesting(true);
    try {
      const response = await SuggestCategorySectionsAPI();
      const r = mergeSuggestions(selected, doubts, response?.data?.suggestions);
      setSelected(r.selected);
      setDoubts(r.doubts);
      if (r.filled === 0 && r.doubtful === 0) {
        warningToaster(t("cicImport.noSuggestion"));
      } else {
        successToaster(
          `${t("cicImport.ready", { count: r.filled })}${r.doubtful ? t("cicImport.readyDoubtful", { count: r.doubtful }) : ""}${t("cicImport.readyEnd")}`
        );
      }
    } catch (error) {
      ErrorToaster(error?.message || t("cicImport.suggestUnavailable"));
    } finally {
      setSuggesting(false);
    }
  };

  const handleImport = async () => {
    if (selected?.length > 0) {
      try {
        setIsLoading(true);
        const response = await ImportCicProductsApi(selected);
        if (response.success) {
          successToaster(t("cicImport.imported"));
          setSelected([]);
          setDoubts({});
        } else {
          ErrorToaster(t("cicImport.importFailed"));
        }
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
      } finally {
        setIsLoading(false);
      }
    } else {
      warningToaster(t("cicImport.selectOne"));
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex flex-column w-100 justify-content-center align-items-center"
        style={{ height: "calc(100vh - 260px)" }}
      >
        <Spinner style={{ width: "3rem", height: "3rem" }} />
      </div>
    );
  }
  return sections?.length > 0 ? (
    <div className="content w-100">
      <Card>
        <Container className="text-center p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted small text-left">
              {t("cicImport.hint")}
            </span>
            <Button color="info" size="sm" onClick={suggestWithAI} disabled={suggesting || isLoadingCategory}>
              {suggesting ? t("cicImport.thinking") : t("cicImport.suggest")}
            </Button>
          </div>
          {Object.keys(doubts).length > 0 && (
            <div className="alert alert-warning text-left small">
              {t("cicImport.doubtful", { count: Object.keys(doubts).length })}
            </div>
          )}
          <div className="table-responsive">
            <Table bordered className="category-table">
              <thead>
                <tr>
                  <th className="text-left">{t("cicImport.colCategory")}</th>
                  {sections.map((col) => (
                    <th key={col?.uuid} id={col?.uuid}>
                      {col?.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoadingCategory ? (
                  <SkeletonTheme
                    baseColor={isDarkMode ? "#ebebeb" : "#2c2c3a"}
                    highlightColor={isDarkMode ? "#f5f5f5" : "#3a3a4a"}
                    borderRadius="0.5rem"
                    duration={1.4}
                    height={"23px"}
                  >
                    {Array.from({ length: 10 }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        {[...Array(7)].map((_, index) => (
                          <td key={index}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </SkeletonTheme>
                ) : categoriesData?.length === 0 ? (
                  <tr>
                    <td colSpan={sections?.length + 1} className="text-center">
                      {t("cicImport.noRecords")}
                    </td>
                  </tr>
                ) : (
                  categoriesData.map((category) => (
                    <tr key={category.cic_category_id}>
                      <td className="text-left">
                        {`${category.name} (${category.product_count || 0})`}
                        {doubts[category.cic_category_id] && (
                          <span
                            className="ml-2 text-warning"
                            title={doubts[category.cic_category_id]}
                          >
                            ⚠ <small>{doubts[category.cic_category_id]}</small>
                          </span>
                        )}
                      </td>
                      {sections.map((col, index) => (
                        <td key={index} className="text-center">
                          <FormGroup check>
                            <Label check>
                              <Input
                                type="checkbox"
                                checked={selected?.some(
                                  (item) =>
                                    item.category_id ===
                                      category.cic_category_id &&
                                    item.section_ids.includes(col?.uuid)
                                )}
                                onChange={() =>
                                  toggleCheckbox(
                                    category.cic_category_id,
                                    category.name,
                                    col?.uuid
                                  )
                                }
                              />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              color="primary"
              className="mt-3 text-capitalize"
              onClick={handleImport}
              disabled={isLoadingCategory || categoriesData?.length === 0}
            >
              {t("cicImport.import")}
            </Button>
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              isLoading={isLoadingCategory}
            />
          </div>
        </Container>
      </Card>
    </div>
  ) : (
    ""
  );
};

export default CategoryTableCic;
